/* eslint-disable max-lines-per-function */
import { EditorID } from 'Cloud/Application/Editor/types';
import classNames from 'clsx';
import mascotDark from 'img/empty-folder/dark/mascot.png';
import mascot from 'img/empty-folder/light/mascot.png';
import EmptyAllDocsIconDark from 'img/empty-state/dark/empty_all_docs.png';
import TrashIconDark from 'img/empty-state/dark/trash.png';
import EmptyAllDocsIcon from 'img/empty-state/empty_all_docs.png';
import TrashIcon from 'img/empty-state/trash.png';
import { ReactComponent as AppQRCode } from 'img/errors/app-qrcode.svg';
import { ReactComponent as AppQRCodeDark } from 'img/errors/dark/app-qrcode.svg';
import { ReactComponent as EmptyAttachIcon } from 'img/icons/emptyAttach.svg';
import { ReactComponent as LoopIcon } from 'img/icons/loop.svg';
import { ReactComponent as EmptyFolderIcon } from 'img/icons/uploadStroke.svg';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainRestrictionsForItems } from 'reactApp/appHelpers/appHelpers';
import { EDITORS_CONFIG, ENABLE_FULL_RESPONSIVE, IS_ONPREMISE } from 'reactApp/appHelpers/configHelpers';
import { inputType } from 'reactApp/appHelpers/featuresHelpers/features/uploadNewFileApi';
import { publishHelper } from 'reactApp/appHelpers/publishHelper';
import { toolbarActions } from 'reactApp/appHelpers/toolbarActions';
import { useViewContext } from 'reactApp/hocs/withViewContext';
import { useIsTutoriaIntegration } from 'reactApp/hooks/useIsTutoriaIntegration';
import { AllDocumentsCategoryType, EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { getBizCurrentCategory } from 'reactApp/modules/bizCategories/bizCategories.selectors';
import { BizCategoryType } from 'reactApp/modules/bizCategories/bizCategories.types';
import { getDomainFoldersFilterActive } from 'reactApp/modules/home/home.selectors';
import type { EViewMode } from 'reactApp/modules/settings/settings.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { setButtonTypeClickAction } from 'reactApp/modules/upload/upload.module';
import { useUploadInputHandlers } from 'reactApp/modules/uploading/hooks/useUploadInputHandlers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { ErrorPageButton } from 'reactApp/sections/ErrorPage/components/ErrorPageButton';
import { Button, ButtonSizeMode } from 'reactApp/ui/Button/Button';
import { ButtonLink } from 'reactApp/ui/ButtonLink/ButtonLink';
import { CopyWeblink } from 'reactApp/ui/CopyWeblink/CopyWeblink';
import { FileIcon } from 'reactApp/ui/FileIcon/FileIcon';
import { LinkIcon } from 'reactApp/ui/VKUIIcons';
import { sendEmptyStatesAnalitics } from 'reactApp/utils/emptyStatesGa';
import { sendGa } from 'reactApp/utils/ga';

import styles from './Empty.css';
import { Empty as EmptyNew } from './new/Empty';

interface HomeProps {
    type: 'folder';
    storage: EStorageType;
    canUpload?: boolean;
    canCoshare?: boolean;
    weblink?: string;
    id?: string;
    currentAllDocsType?: EAllDocumentsType | null;
    currentAllDocsCategory?: AllDocumentsCategoryType | null;
    isNewTrashbin?: boolean;
    domainFoldersFilterActive?: boolean;
}

type Props = {
    viewMode?: EViewMode;
} & (
    | HomeProps
    | {
          type: 'gallery';
          searchType?: string;
          storage: EStorageType;
          id?: string;
          canUpload?: boolean;
          isNewTrashbin?: boolean;
      }
);

const getDocTypeName = (type?: EAllDocumentsType | null, isPlural = true) => {
    switch (type) {
        case EAllDocumentsType.document:
            return isPlural ? 'документы' : 'документ';
        case EAllDocumentsType.spreadsheet:
            return isPlural ? 'таблицы' : 'таблицу';
        case EAllDocumentsType.presentation:
            return isPlural ? 'презентации' : 'презентацию';
        case EAllDocumentsType.pdf:
            return isPlural ? 'PDF-файлы' : 'PDF-файл';
        default:
            return '';
    }
};

const getDocTypeText = (type?: EAllDocumentsType | null, category?: AllDocumentsCategoryType | null) => {
    if (category === AllDocumentsCategoryType.youShared) {
        return '';
    }

    if (category === AllDocumentsCategoryType.sharedWithYou) {
        switch (type) {
            case EAllDocumentsType.document:
                return 'Здесь будут документы, к которым вам дадут доступ через email';
            case EAllDocumentsType.spreadsheet:
                return 'Здесь будут таблицы, к которым вам дадут доступ через email';
            case EAllDocumentsType.presentation:
                return 'Здесь будут презентации, к которым вам дадут доступ через email';
            case EAllDocumentsType.pdf:
                return 'Здесь будут файлы, к которым вам дадут доступ через email';
            default:
                return '';
        }
    }

    switch (type) {
        case EAllDocumentsType.document:
            return 'Создавайте и редактируйте документы\nонлайн вместе с другими людьми';
        case EAllDocumentsType.spreadsheet:
            return 'Создавайте и редактируйте таблицы онлайн\nвместе с другими людьми';
        case EAllDocumentsType.presentation:
            return 'Создавайте и редактируйте презентации онлайн\nвместе с другими людьми';
        case EAllDocumentsType.pdf:
            return 'Загрузите файлы, и они станут доступны\nдля редактирования';
        default:
            return '';
    }
};

const getIcon = (props: Props, isB2BUser: boolean, isDarkTheme: boolean) => {
    const { storage, canUpload } = props;

    if (storage === EStorageType.feed || storage === EStorageType.quotaCleaner) {
        return <LoopIcon />;
    }

    if (storage === EStorageType.trashbin) {
        return <img src={isDarkTheme ? TrashIconDark : TrashIcon} alt="Пустая корзина" width="360" />;
    } else if (storage === EStorageType.attaches) {
        return <EmptyAttachIcon />;
    } else if (storage === EStorageType.gallery) {
        return null;
    } else if (storage === EStorageType.alldocuments || storage === EStorageType.incomingPublic) {
        return <img src={isDarkTheme ? EmptyAllDocsIconDark : EmptyAllDocsIcon} alt="Пустой раздел документов" width="360" />;
    } else if (storage === EStorageType.public && props.type === 'folder' && !isB2BUser) {
        return <img src={isDarkTheme ? mascotDark : mascot} alt="" width="360" />;
    } else if (canUpload && !isB2BUser) {
        return <EmptyFolderIcon />;
    }
};

const getAllDocumentsTitle = (type?: EAllDocumentsType | null, category?: AllDocumentsCategoryType | null) => {
    if (category === AllDocumentsCategoryType.youShared) {
        switch (type) {
            case EAllDocumentsType.document:
                return 'Вы пока не поделились документами';
            case EAllDocumentsType.spreadsheet:
                return 'Вы пока не поделились таблицами';
            case EAllDocumentsType.presentation:
                return 'Вы пока не поделились презентациями';
            case EAllDocumentsType.pdf:
                return 'Вы пока не поделились файлами';
        }
    }

    if (category === AllDocumentsCategoryType.sharedWithYou) {
        switch (type) {
            case EAllDocumentsType.document:
                return 'Пока нет доступных документов';
            case EAllDocumentsType.spreadsheet:
                return 'Пока нет доступных таблиц';
            case EAllDocumentsType.presentation:
                return 'Пока нет доступных презентаций';
            case EAllDocumentsType.pdf:
                return 'Пока нет доступных файлов';
        }
    }

    return 'Пока ничего нет';
};

const getTitle = (props: Props, bizCurrentCategory?: BizCategoryType) => {
    const bizTitle = 'По выбранным параметрам ничего не найдено';
    const emptyBizCategory = !bizCurrentCategory || bizCurrentCategory === BizCategoryType.all;
    const { isDomainAndAccessDenied } = getDomainRestrictionsForItems(props.id);

    if (isDomainAndAccessDenied) {
        return 'Нет доступа в папку, обратитесь к администратору';
    }

    if (props.type === 'gallery') {
        const { searchType } = props;

        if (!emptyBizCategory) {
            return bizTitle;
        }

        return searchType === 'mobile' ? 'Сейчас у вас нет мобильных фотографий' : 'Сейчас у вас нет фотографий';
    }

    const { storage, isNewTrashbin } = props;

    if (!emptyBizCategory && !props.domainFoldersFilterActive) {
        return bizTitle;
    }

    if (storage === EStorageType.trashbin) {
        return isNewTrashbin ? 'В корзине пусто' : 'Корзина пуста';
    } else if (storage === EStorageType.attaches) {
        return 'Сейчас у вас нет файлов из почты';
    } else if (storage === EStorageType.feed) {
        return 'По запросу ничего не найдено';
    } else if (storage === EStorageType.alldocuments) {
        return getAllDocumentsTitle(props.currentAllDocsType, props.currentAllDocsCategory);
    } else if (storage === EStorageType.sharedIncoming) {
        return (
            <>
                С вами пока ничем не поделились
                <div
                    className={classNames(styles.text, {
                        [styles.textResponsive]: ENABLE_FULL_RESPONSIVE,
                    })}
                >
                    здесь появятся файлы и папки, которыми с вами поделятся
                </div>
            </>
        );
    } else if (storage === EStorageType.incomingPublic) {
        return 'Пока нет доступных файлов';
    }

    return 'В папке пока ничего нет';
};

const getText = (props: Props, onUploadClick, inputRef) => {
    const { storage, canUpload, isNewTrashbin, type } = props;

    if (storage === EStorageType.trashbin) {
        return isNewTrashbin
            ? 'Перенесите ненужные документы в корзину.\nФайлы хранятся тут 14 дней, а затем удаляются'
            : 'Вы всегда можете удалить файлы из корзины, чтобы освободить место';
    } else if (storage === EStorageType.attaches) {
        return 'Все файлы из ваших почтовых переписок будут автоматически попадать сюда';
    } else if (storage === EStorageType.alldocuments && props.type === 'folder') {
        return getDocTypeText(props.currentAllDocsType, props.currentAllDocsCategory);
    } else if (canUpload) {
        return (
            <>
                Перенесите файлы сюда или нажмите
                <br />
                на кнопку{' '}
                <ButtonLink onClick={onUploadClick} fontInherit primary>
                    Загрузить
                </ButtonLink>
                <input type={inputType} multiple className={styles.input} ref={inputRef} />
            </>
        );
    } else if (storage === EStorageType.public && type === 'folder') {
        return 'Как только владелец загрузит файлы,\nвы увидите их здесь';
    } else if (storage === EStorageType.incomingPublic) {
        return 'Здесь будут файлы, к которым вам дадут доступ через email';
    }
};

const CreateTypeMap = {
    document: 'docx',
    spreadsheet: 'xlsx',
    presentation: 'pptx',
};

export const EmptyComponent = memo((props: Props): ReactElement => {
    const { storage = EStorageType.search, isNewTrashbin, type } = props;
    const { canCoshare, weblink, id = '', currentAllDocsType, currentAllDocsCategory } = props as HomeProps;
    const inputRef = useRef<HTMLInputElement>(null);
    const dispatch = useDispatch();
    const domainFoldersFilterActive = useSelector(getDomainFoldersFilterActive);
    const viewContext = useViewContext();
    const bizCurrentCategory = useSelector(getBizCurrentCategory);
    const isB2BUser = useSelector(UserSelectors.isB2BUser);
    const isR7Editor = EDITORS_CONFIG[0]?.id === EditorID.R7_WOPI || EDITORS_CONFIG[0]?.id === EditorID.R7;
    const canCreatePresentation =
        isR7Editor && EDITORS_CONFIG[0]?.edit_extensions.includes('pptx') && EDITORS_CONFIG[0]?.create_extensions.includes('pptx');
    const isDarkTheme = useSelector(isCurrentDarkTheme);
    const isReadOnlyBizFolder = isB2BUser;
    useEffect(() => {
        sendEmptyStatesAnalitics({ action: 'show' });
    }, []);
    const { isDomainAndAccessDenied } = getDomainRestrictionsForItems(props.id);

    const canCreateDoc =
        storage === EStorageType.alldocuments &&
        currentAllDocsCategory !== AllDocumentsCategoryType.youShared &&
        currentAllDocsCategory !== AllDocumentsCategoryType.sharedWithYou &&
        (currentAllDocsType === EAllDocumentsType.document ||
            currentAllDocsType === EAllDocumentsType.spreadsheet ||
            (currentAllDocsType === EAllDocumentsType.presentation && canCreatePresentation));

    const isTutoriaIntegration = useIsTutoriaIntegration();

    const onUploadClick = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.click();
        }
    }, [inputRef.current]);

    const handlePublishing = useCallback(() => {
        sendGa('empty-foldr', 'click', 'publish');
        publishHelper({ item: { id } });
    }, []);

    const handleCopy = useCallback(() => {}, []);

    const handleCreateDocs = useCallback(
        () => toolbarActions.create(currentAllDocsType ? CreateTypeMap[currentAllDocsType] : 'docx', 'empty', true, 'docs'),
        []
    );

    const canUpload = props?.canUpload;

    useUploadInputHandlers({ input: canUpload ? inputRef : null });

    const fileIconType = useMemo(() => {
        if (storage === EStorageType.gallery) {
            return 'image';
        }

        if (viewContext.isVKEmbedded) {
            return type;
        }

        return '';
    }, [storage, type, viewContext.isVKEmbedded]);

    useEffect(() => {
        sendGa('empty-foldr', 'show');
        dispatch(setButtonTypeClickAction('click'));
    }, [id]);

    const icon = getIcon(props, isB2BUser, isDarkTheme);

    return (
        <div
            className={classNames(styles.root, {
                [styles.rootNewBin]: isNewTrashbin,
                [styles.rootEmbedded]: viewContext.isVKEmbedded,
                [styles.rootIntegration]: isTutoriaIntegration,
                [styles.rootPublic]: storage === EStorageType.public && !isB2BUser,
            })}
        >
            {!isDomainAndAccessDenied && (
                <div
                    className={classNames(styles.file, {
                        [styles.emptyBizPublic]: isB2BUser && storage === EStorageType.public,
                    })}
                >
                    {storage !== EStorageType.alldocuments &&
                    !isNewTrashbin &&
                    (storage === EStorageType.gallery || viewContext.isVKEmbedded) ? (
                        <FileIcon size="l" className={styles.unknownIconUrl} type={fileIconType} />
                    ) : (
                        icon && <div className={styles.icon}>{icon}</div>
                    )}
                </div>
            )}
            <div className={classNames(styles.title, { [styles.titleEmbedded]: viewContext.isVKEmbedded })}>
                {getTitle(props, bizCurrentCategory)}
            </div>
            {!isDomainAndAccessDenied && (
                <div
                    className={classNames(styles.text, {
                        [styles.textResponsive]: ENABLE_FULL_RESPONSIVE,
                        [styles.textIntegration]: isTutoriaIntegration,
                    })}
                >
                    {getText(props, onUploadClick, inputRef)}
                </div>
            )}
            {storage === EStorageType.home && !bizCurrentCategory && canCoshare && !domainFoldersFilterActive && (
                <div className={styles.controls}>
                    {weblink ? (
                        <CopyWeblink publicUrl={weblink} onCopy={handleCopy} onClick={handlePublishing} theme="folder" />
                    ) : (
                        !isReadOnlyBizFolder && (
                            <>
                                <div className={styles.link}>
                                    <LinkIcon height={20} width={20} />
                                </div>
                                <div className={styles.publish} onClick={handlePublishing}>
                                    Поделиться папкой по ссылке
                                </div>
                            </>
                        )
                    )}
                </div>
            )}
            {canCreateDoc && (
                <div className={styles.controls}>
                    <Button theme="vk" sizeMode={ButtonSizeMode.small} onClick={handleCreateDocs}>
                        Создать {getDocTypeName(currentAllDocsType, false)}
                    </Button>
                </div>
            )}
            {storage === EStorageType.public && type === 'folder' && !isDomainAndAccessDenied && (
                <>
                    <div className={styles.controls}>
                        <ErrorPageButton link="/" text="Перейти на главную" compact />
                    </div>
                    {!isB2BUser && !IS_ONPREMISE && (
                        <div className={styles.qrcodeContainer}>
                            <div className={styles.qrcode}>{isDarkTheme ? <AppQRCodeDark /> : <AppQRCode />}</div>
                            <div className={styles.qrcodeText}>
                                Установите приложение, чтобы ваши файлы и&nbsp;фото всегда были под рукой
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

EmptyComponent.displayName = 'Empty';

export const Empty = Object.assign(EmptyComponent, { New: EmptyNew });
